/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { Observable } from "rxjs";

import Api, { ApiStatus } from "../Api";

import CompanyDataProvider from "./CompanyDataProvider";

import { CompanyAssetGroupFilterDescModel, CompanyAssetGroupFilterModel, CompanyAssetGroupModel, CompanyAssetGroupScheduleModel, CompanyGroupModel, CompanyModel, CompanyUserModel } from "data/models/CompanyModel";

import { CompaniesDTO } from "data/dto/company/CompaniesDTO";
import { UsersDTO } from "data/dto/company/UsersDTO";
import { GroupsDTO } from "data/dto/company/GroupsDTO";
import { AssetGroupsDTO } from "data/dto/company/AssetGroupsDTO";
import { CompanyDTO } from "data/dto/company/CompanyDTO";
import { FilterGroupScheduleDTO } from "data/dto/company/FilterGroupScheduleDTO";
import { FilterGroupSchedulesDTO } from "data/dto/company/FilterGroupSchedulesDTO";
import { FiltersDTO } from "data/dto/company/FiltersDTO";
import { UserDTO } from "data/dto/company/UserDTO";
import { GroupDTO } from "data/dto/company/GroupDTO";
import { AssetGroupDTO } from "data/dto/company/AssetGroupDTO";
import { FilterDTO } from "data/dto/company/FilterDTO";
import { FiltersCopyDTO } from "data/dto/company/FilterCopyDTO";
import { FilterDescsDTO } from "data/dto/company/FilterDescsDTO";
import { FilterDescDTO } from "data/dto/company/FilterDescDTO";
import { CveListDTO } from "data/dto/company/CveListDTO";

export default class CompanyDataProviderDefault implements CompanyDataProvider {

   constructor(
      private api: Api
   ) { }

   /**
    * Returns observable of list of companies to which the currently logged user is assigned
    * GET /companies
    * @returns Observable of list of company ids to which the currently logged user is assigned
    */
   public listCompanies(): Observable<CompanyModel[]> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyModel[]>(

         s => {

            this.api.call<void, CompaniesDTO>("/companies").subscribe({
               next: o => {
                  const cm: CompanyModel[] = o.companies.map(c => {
                     return {
                        _seq_no: c._seq_no!,
                        _primary_term: c._primary_term!,
                        id: c.id!,
                        companyName: c.companyName,
                        addressStreet1: c.addressStreet1,
                        addressStreet2: c.addressStreet2,
                        addressState: c.addressState,
                        addressCity: c.addressCity,
                        addressZip: c.addressZip,
                        addressCountry: c.addressCountry,
                        taxId: c.taxId,
                        taxVatId: c.taxVatId,
                        technicalContactName: c.technicalContactName,
                        technicalContactEmail: c.technicalContactEmail,
                        financialContactName: c.financialContactName,
                        financialContactEmail: c.financialContactEmail,
                     }
                  });
                  s.next(cm);
                  s.complete();

               },
               error: e => {
                  s.error(e);
               }
            });

         }

      )

   }

   // POST /companies
   public createCompany(company: CompanyModel): Observable<CompanyModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyModel>(

         s => {

            const data: CompanyDTO = {
               companyName: company.companyName,
               addressStreet1: company.addressStreet1,
               addressStreet2: company.addressStreet2,
               addressCity: company.addressCity,
               addressState: company.addressState,
               addressCountry: company.addressCountry,
               addressZip: company.addressZip,
               taxId: company.taxId,
               taxVatId: company.taxVatId,
               technicalContactName: company.technicalContactName,
               technicalContactEmail: company.technicalContactEmail,
               financialContactName: company.financialContactName,
               financialContactEmail: company.financialContactEmail
            }

            this.api.call<CompanyDTO, CompanyDTO>(`/companies`, undefined, "POST", data).subscribe({

               next: c => {

                  s.next({
                     _seq_no: c._seq_no!,
                     _primary_term: c._primary_term!,
                     id: c.id!,
                     companyName: c.companyName,
                     addressStreet1: c.addressStreet1,
                     addressStreet2: c.addressStreet2,
                     addressState: c.addressState,
                     addressCity: c.addressCity,
                     addressZip: c.addressZip,
                     addressCountry: c.addressCountry,
                     taxId: c.taxId,
                     taxVatId: c.taxVatId,
                     technicalContactName: c.technicalContactName,
                     technicalContactEmail: c.technicalContactEmail,
                     financialContactName: c.financialContactName,
                     financialContactEmail: c.financialContactEmail,
               })
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // GET /companies/:companyId
   public readCompany(companyId: string): Observable<CompanyModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyModel>(

         s => {

            this.api.call<void, CompanyDTO>(`/companies/${companyId}`).subscribe({

               next: c => {

                  s.next({
                     _seq_no: c._seq_no!,
                     _primary_term: c._primary_term!,
                     id: c.id!,
                     companyName: c.companyName,
                     addressStreet1: c.addressStreet1,
                     addressStreet2: c.addressStreet2,
                     addressState: c.addressState,
                     addressCity: c.addressCity,
                     addressZip: c.addressZip,
                     addressCountry: c.addressCountry,
                     taxId: c.taxId,
                     taxVatId: c.taxVatId,
                     technicalContactName: c.technicalContactName,
                     technicalContactEmail: c.technicalContactEmail,
                     financialContactName: c.financialContactName,
                     financialContactEmail: c.financialContactEmail,
               })
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });
         }

      );

   }

   // POST /companies/:companyId
   public updateCompany(company: CompanyModel): Observable<CompanyModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyModel>(

         s => {

            const data: CompanyDTO = {
               _seq_no: company._seq_no,
               _primary_term: company._primary_term,
               id: company.id,
               companyName: company.companyName,
               addressStreet1: company.addressStreet1,
               addressStreet2: company.addressStreet2,
               addressCity: company.addressCity,
               addressState: company.addressState,
               addressCountry: company.addressCountry,
               addressZip: company.addressZip,
               taxId: company.taxId,
               taxVatId: company.taxVatId,
               technicalContactName: company.technicalContactName,
               technicalContactEmail: company.technicalContactEmail,
               financialContactName: company.financialContactName,
               financialContactEmail: company.financialContactEmail
            }

            this.api.call<CompanyDTO, CompanyDTO>(`/companies/${company.id}`, undefined, "POST", data).subscribe({

               next: c => {

                  s.next({
                     _seq_no: c._seq_no!,
                     _primary_term: c._primary_term!,
                     id: c.id!,
                     companyName: c.companyName,
                     addressStreet1: c.addressStreet1,
                     addressStreet2: c.addressStreet2,
                     addressState: c.addressState,
                     addressCity: c.addressCity,
                     addressZip: c.addressZip,
                     addressCountry: c.addressCountry,
                     taxId: c.taxId,
                     taxVatId: c.taxVatId,
                     technicalContactName: c.technicalContactName,
                     technicalContactEmail: c.technicalContactEmail,
                     financialContactName: c.financialContactName,
                     financialContactEmail: c.financialContactEmail,
               });
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // DELETE /companies/:companyId
   public deleteCompany(companyId: string): Observable<void> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<void>(

         s => {

            this.api.call<void, void>(`/companies/${companyId}`, undefined, "DELETE").subscribe({

               next: o => {
                  s.next();
                  s.complete();
               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }



   // GET /companies/:companyId/users
   public listUsers(companyId: string): Observable<CompanyUserModel[]> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyUserModel[]>(

         s => {

            this.api.call<void, UsersDTO>(`/companies/${companyId}/users`).subscribe({

               next: o => {

                  const um: CompanyUserModel[] = o.users.map(u => {
                     return {
                        _seq_no: u._seq_no!,
                        _primary_term: u._primary_term!,
                        companyId: companyId,
                        id: u.id!,
                        username: u.username,
                        role: u.role,
                        groups: u.groups,
                        lastInvitation: u.lastInvitation
                     }
                  });
                  s.next(um);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      )

   }

   // POST /companies/:companyId/users
   public createUser(user: CompanyUserModel): Observable<CompanyUserModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyUserModel>(

         s => {

            const data: UserDTO = {
               username: user.username,
               role: user.role,
               groups: user.groups,
               lastInvitation: 0
            };

            this.api.call<UserDTO, UserDTO>(`/companies/${user.companyId}/users`, undefined, "POST", data).subscribe({

               next: o => {

                  const um: CompanyUserModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: user.companyId,
                     id: o.id!,
                     username: o.username,
                     role: o.role,
                     groups: o.groups,
                     lastInvitation: user.lastInvitation
                  }
                  s.next(um);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // GET /companies/:companyId/users/:userId
   public readUser(companyId: string, userId: string): Observable<CompanyUserModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyUserModel>(

         s => {

            this.api.call<void, UserDTO>(`/companies/${companyId}/users/${userId}`).subscribe({

               next: o => {

                  const um: CompanyUserModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: companyId,
                     id: o.id!,
                     username: o.username,
                     role: o.role,
                     groups: o.groups,
                     lastInvitation: o.lastInvitation
                  }
                  s.next(um);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // POST /companies/:companyId/users/:userId
   public updateUser(user: CompanyUserModel): Observable<CompanyUserModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyUserModel>(

         s => {

            const data: UserDTO = {
               _seq_no: user._seq_no,
               _primary_term: user._primary_term,
               id: user.id,
               username: user.username,
               role: user.role,
               groups: user.groups,
               lastInvitation: -1
            };

            this.api.call<UserDTO, UserDTO>(`/companies/${user.companyId}/users/${user.id}`, undefined, "POST", data).subscribe({

               next: o => {

                  s.next({
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: user.companyId,
                     id: o.id!,
                     username: o.username,
                     role: o.role,
                     groups: o.groups,
                     lastInvitation: o.lastInvitation
                  });
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // DELETE /companies/:companyId/users/:userId
   public deleteUser(companyId: string, userId: string): Observable<void> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<void>(

         s => {

            this.api.call<void, void>(`/companies/${companyId}/users/${userId}`, undefined, "DELETE").subscribe({

               next: o => {

                  s.next();
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }


   // GET /companies/:companyId/users/:userId/invite
   public inviteUser(companyId: string, userId: string): Observable<CompanyUserModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyUserModel>(

         s => {

            this.api.call<void, UserDTO>(`/companies/${companyId}/users/${userId}/invite`, undefined, "GET").subscribe({

               next: o => {

                  s.next({
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: companyId,
                     id: o.id!,
                     username: o.username,
                     role: o.role,
                     groups: o.groups,
                     lastInvitation: o.lastInvitation
                  });
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }


   // GET /companies/:companyId/groups
   public listGroups(companyId: string): Observable<CompanyGroupModel[]> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyGroupModel[]>(

         s => {

            this.api.call<void, GroupsDTO>(`/companies/${companyId}/groups`).subscribe({

               next: o => {

                  const gm: CompanyGroupModel[] = o.groups.map(g => {
                     return {
                        _seq_no: g._seq_no!,
                        _primary_term: g._primary_term!,
                        companyId: companyId,
                        id: g.id!,
                        name: g.name,
                        description: g.description
                     }
                  });
                  s.next(gm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      )

   }

   // POST /companies/:companyId/groups
   public createGroup(group: CompanyGroupModel): Observable<CompanyGroupModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyGroupModel>(

         s => {

            const data: GroupDTO = {
               name: group.name,
               description: group.description
            };

            this.api.call<GroupDTO, GroupDTO>(`/companies/${group.companyId}/groups`, undefined, "POST", data).subscribe({

               next: o => {

                  const gm: CompanyGroupModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: group.companyId,
                     id: o.id!,
                     name: o.name,
                     description: o.description
                  }
                  s.next(gm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // GET /companies/:companyId/groups/:groupId
   public readGroup(companyId: string, groupId: string): Observable<CompanyGroupModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyGroupModel>(

         s => {

            this.api.call<void, GroupDTO>(`/companies/${companyId}/groups/${groupId}`).subscribe({

               next: o => {

                  const gm: CompanyGroupModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: companyId,
                     id: o.id!,
                     name: o.name,
                     description: o.description
                  }
                  s.next(gm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // POST /companies/:companyId/groups/:groupId
   public updateGroup(group: CompanyGroupModel): Observable<CompanyGroupModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyGroupModel>(

         s => {

            const data: GroupDTO = {
               _seq_no: group._seq_no,
               _primary_term: group._primary_term,
               id: group.id,
               name: group.name,
               description: group.description
            };

            this.api.call<GroupDTO, GroupDTO>(`/companies/${group.companyId}/groups/${group.id}`, undefined, "POST", data).subscribe({

               next: o => {

                  s.next({
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: group.companyId,
                     id: o.id!,
                     name: o.name,
                     description: o.description
                  });
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // DELETE /companies/:companyId/groups/:groupId
   public deleteGroup(companyId: string, groupId: string): Observable<void> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<void>(

         s => {

            this.api.call<void, void>(`/companies/${companyId}/groups/${groupId}`, undefined, "DELETE").subscribe({

               next: o => {

                  s.next();
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }



   // GET /companies/:companyId/filter-groups
   public listAssetGroups(companyId: string): Observable<CompanyAssetGroupModel[]> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupModel[]>(

         s => {

            this.api.call<void, AssetGroupsDTO>(`/companies/${companyId}/filter-groups`).subscribe({

               next: o => {

                  const fgm: CompanyAssetGroupModel[] = o.filterGroups.map(fg => {
                     return {
                        _seq_no: fg._seq_no!,
                        _primary_term: fg._primary_term!,
                        companyId: companyId,
                        id: fg.id!,
                        name: fg.name,
                        description: fg.description,
                        owner: fg.owner,
                        editPermissionsGroups: fg.editPermissionsGroups,
                        readPermissionsGroups: fg.readPermissionsGroups
                     }
                  });
                  s.next(fgm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      )

   }

   // GET /companies/:companyId/filter-groups?hasWritePermissions=:filterGroupName
   public checkAssetGroupWritePermissions(companyId: string, filterGroupName: string): Observable<boolean> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<boolean>(

         s => {

            this.api.call<void, { result: boolean }>(`/companies/${companyId}/filter-groups?hasWritePermissions=${filterGroupName}`).subscribe({

               next: o => {
                  s.next(o.result);
                  s.complete();
               },

               error: e => {
                  s.error(e);
               }

            });

         }

      )

   }


   // POST /companies/:companyId/filter-groups
   public createAssetGroup(companyId: string, name: string, description: string): Observable<CompanyAssetGroupModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupModel>(

         s => {

            const data: AssetGroupDTO = {
               name: name,
               description: description,
               owner: "",
               editPermissionsGroups: [],
               readPermissionsGroups: []
            };

            this.api.call<AssetGroupDTO, AssetGroupDTO>(`/companies/${companyId}/filter-groups`, undefined, "POST", data).subscribe({

               next: (o: AssetGroupDTO) => {

                  const fgm: CompanyAssetGroupModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: companyId,
                     id: o.id!,
                     name: o.name,
                     description: o.description,
                     owner: o.owner,
                     editPermissionsGroups: o.editPermissionsGroups,
                     readPermissionsGroups: o.readPermissionsGroups
                  }

                  s.next(fgm);
                  s.complete();

               },

               error: (e: any) => {
                  s.error(e);
               }

            });

         }

      );

   }

   // GET /companies/:companyId/filter-groups/:filterGroupId
   public readAssetGroup(companyId: string, filterGroupId: string): Observable<CompanyAssetGroupModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupModel>(

         s => {

            this.api.call<void, AssetGroupDTO>(`/companies/${companyId}/filter-groups/${filterGroupId}`).subscribe({

               next: o => {

                  const fgm: CompanyAssetGroupModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: companyId,
                     id: o.id!,
                     name: o.name,
                     description: o.description,
                     owner: o.owner,
                     editPermissionsGroups: o.editPermissionsGroups,
                     readPermissionsGroups: o.readPermissionsGroups
                  }
                  s.next(fgm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // POST /companies/:companyId/filter-groups/:filterGroupId
   public updateAssetGroup(filterGroup: CompanyAssetGroupModel): Observable<CompanyAssetGroupModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupModel>(

         s => {

            const data: AssetGroupDTO = {
               _seq_no: filterGroup._seq_no,
               _primary_term: filterGroup._primary_term,
               id: filterGroup.id,
               name: filterGroup.name,
               description: filterGroup.description,
               owner: filterGroup.owner,
               editPermissionsGroups: filterGroup.editPermissionsGroups,
               readPermissionsGroups: filterGroup.readPermissionsGroups
            };

            this.api.call<AssetGroupDTO, AssetGroupDTO>(`/companies/${filterGroup.companyId}/filter-groups/${filterGroup.id}`, undefined, "POST", data).subscribe({

               next: o => {

                  s.next({
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: filterGroup.companyId,
                     id: o.id!,
                     name: o.name,
                     description: o.description,
                     owner: o.owner,
                     editPermissionsGroups: o.editPermissionsGroups,
                     readPermissionsGroups: o.readPermissionsGroups
                  });
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );
   }

   // DELETE /companies/:companyId/filter-groups/:filterGroupId
   public deleteAssetGroup(companyId: string, filterGroupId: string): Observable<void> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<void>(

         s => {

            this.api.call<void, void>(`/companies/${companyId}/filter-groups/${filterGroupId}`, undefined, "DELETE").subscribe({

               next: o => {

                  s.next();
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }



   // GET /companies/:companyId/filter-groups/:filterGroupId/schedules
   public listAssetGroupsSchedules(companyId: string, filterGroupId: string): Observable<CompanyAssetGroupScheduleModel[]> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupScheduleModel[]>(

         s => {

            this.api.call<void, FilterGroupSchedulesDTO>(`/companies/${companyId}/filter-groups/${filterGroupId}/schedules`).subscribe({

               next: o => {

                  const fgsm: CompanyAssetGroupScheduleModel[] = o.schedules.map(fgs => {
                     return {
                        _seq_no: fgs._seq_no!,
                        _primary_term: fgs._primary_term!,
                        companyId: companyId,
                        filterGroupId: filterGroupId,
                        id: fgs.id!,
                        severity: fgs.severity,
                        schedule: fgs.schedule,
                        distGroups: fgs.distGroups
                     }
                  });
                  s.next(fgsm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      )

   }

   // POST /companies/:companyId/filter-groups/:filterGroupId/schedules
   public createAssetGroupSchedule(filterGroupSchedule: CompanyAssetGroupScheduleModel): Observable<CompanyAssetGroupScheduleModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupScheduleModel>(

         s => {

            const data: FilterGroupScheduleDTO = {
               severity: filterGroupSchedule.severity,
               schedule: filterGroupSchedule.schedule,
               distGroups: filterGroupSchedule.distGroups
            };

            this.api.call<FilterGroupScheduleDTO, FilterGroupScheduleDTO>(`/companies/${filterGroupSchedule.companyId}/filter-groups/${filterGroupSchedule.filterGroupId}/schedules`, undefined, "POST", data).subscribe({

               next: o => {

                  const fgsm: CompanyAssetGroupScheduleModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: filterGroupSchedule.companyId,
                     filterGroupId: filterGroupSchedule.filterGroupId,
                     id: o.id!,
                     severity: o.severity,
                     schedule: o.schedule,
                     distGroups: o.distGroups
                  }
                  s.next(fgsm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // GET /companies/:companyId/filter-groups/:filterGroupId/schedules/:scheduleId
   public readAssetGroupSchedule(companyId: string, filterGroupId: string, scheduleId: string): Observable<CompanyAssetGroupScheduleModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupScheduleModel>(

         s => {

            this.api.call<void, FilterGroupScheduleDTO>(`/companies/${companyId}/filter-groups/${filterGroupId}/schedules/${scheduleId}`).subscribe({

               next: o => {

                  const fgsm: CompanyAssetGroupScheduleModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: companyId,
                     filterGroupId: filterGroupId,
                     id: o.id!,
                     severity: o.severity,
                     schedule: o.schedule,
                     distGroups: o.distGroups
                  }
                  s.next(fgsm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // POST /companies/:companyId/filter-groups/:filterGroupId/schedules/:scheduleId
   public updateAssetGroupSchedule(filterGroupSchedule: CompanyAssetGroupScheduleModel): Observable<CompanyAssetGroupScheduleModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupScheduleModel>(

         s => {

            const data: FilterGroupScheduleDTO = {
               _seq_no: filterGroupSchedule._seq_no,
               _primary_term: filterGroupSchedule._primary_term,
               id: filterGroupSchedule.id,
               severity: filterGroupSchedule.severity,
               schedule: filterGroupSchedule.schedule,
               distGroups: filterGroupSchedule.distGroups
            };

            this.api.call<FilterGroupScheduleDTO, FilterGroupScheduleDTO>(`/companies/${filterGroupSchedule.companyId}/filter-groups/${filterGroupSchedule.filterGroupId}/schedules/${filterGroupSchedule.id}`, undefined, "POST", data).subscribe({

               next: o => {

                  s.next({
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: filterGroupSchedule.companyId,
                     filterGroupId: filterGroupSchedule.filterGroupId,
                     id: o.id!,
                     severity: o.severity,
                     schedule: o.schedule,
                     distGroups: o.distGroups
                  });
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // DELETE /companies/:companyId/filter-groups/:filterGroupId/schedules/:scheduleId
   public deleteAssetGroupSchedule(companyId: string, filterGroupId: string, scheduleId: string): Observable<void> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<void>(

         s => {

            this.api.call<void, void>(`/companies/${companyId}/filter-groups/${filterGroupId}/schedules/${scheduleId}`, undefined, "DELETE").subscribe({

               next: o => {

                  s.next();
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }



   // GET /companies/:companyId/filter-groups/:filterGroupId/filters
   public listAssetGroupsFilters(companyId: string, filterGroupId: string): Observable<CompanyAssetGroupFilterModel[]> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupFilterModel[]>(

         s => {

            this.api.call<void, FiltersDTO>(`/companies/${companyId}/filter-groups/${filterGroupId}/filters`).subscribe({

               next: o => {

                  const fgfms: CompanyAssetGroupFilterModel[] = o.filters.map(fgf => {
                     return {
                        _seq_no: fgf._seq_no!,
                        _primary_term: fgf._primary_term!,
                        companyId: companyId,
                        filterGroupId: filterGroupId,
                        id: fgf.id!,
                        product: fgf.product,
                        vendor: fgf.vendor
                     }
                  });
                  s.next(fgfms);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      )

   }

   // PUT /companies/:companyId/filter-groups/:filterGroupId/filters:filterId
   public copyAssetGroupFilters(companyId: string, srcFilterGroupId: string, dstFilterGroupId: string, cleanTarget: boolean): Observable<void> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<void>(

         s => {

            const data = {
               srcFilterGroupId,
               cleanTarget
            }

            this.api.call<FiltersCopyDTO, void>(`/companies/${companyId}/filter-groups/${dstFilterGroupId}/filters`, undefined, "PUT", data).subscribe({

               next: o => {
                  s.next();
                  s.complete();
               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }


   // POST /companies/:companyId/filter-groups/:filterGroupId/filters
   public createAssetGroupFilter(filterGroupFilter: CompanyAssetGroupFilterModel): Observable<CompanyAssetGroupFilterModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupFilterModel>(

         s => {

            const data: FilterDTO = {
               vendor: filterGroupFilter.vendor,
               product: filterGroupFilter.product,
            };

            this.api.call<FilterDTO, FilterDTO>(`/companies/${filterGroupFilter.companyId}/filter-groups/${filterGroupFilter.filterGroupId}/filters`, undefined, "POST", data).subscribe({

               next: o => {

                  const fgfm: CompanyAssetGroupFilterModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: filterGroupFilter.companyId,
                     filterGroupId: filterGroupFilter.filterGroupId,
                     id: o.id!,
                     vendor: o.vendor,
                     product: o.product
                  }
                  s.next(fgfm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // GET /companies/:companyId/filter-groups/:filterGroupId/filters/:filterId
   public readAssetGroupFilter(companyId: string, filterGroupId: string, filterId: string): Observable<CompanyAssetGroupFilterModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupFilterModel>(

         s => {

            this.api.call<void, FilterDTO>(`/companies/${companyId}/filter-groups/${filterGroupId}/filters/${filterId}`).subscribe({

               next: o => {

                  const fgfm: CompanyAssetGroupFilterModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: companyId,
                     filterGroupId: filterGroupId,
                     id: o.id!,
                     vendor: o.vendor,
                     product: o.product
                  }
                  s.next(fgfm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // POST /companies/:companyId/filter-groups/:filterGroupId/filters/:filterId
   public updateAssetGroupFilter(filterGroupFilter: CompanyAssetGroupFilterModel): Observable<CompanyAssetGroupFilterModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupFilterModel>(

         s => {

            const data: FilterDTO = {
               _seq_no: filterGroupFilter._seq_no,
               _primary_term: filterGroupFilter._primary_term,
               id: filterGroupFilter.id,
               vendor: filterGroupFilter.vendor,
               product: filterGroupFilter.product
            };

            this.api.call<FilterDTO, FilterDTO>(`/companies/${filterGroupFilter.companyId}/filter-groups/${filterGroupFilter.filterGroupId}/filters/${filterGroupFilter.id}`, undefined, "POST", data).subscribe({

               next: o => {

                  s.next({
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: filterGroupFilter.companyId,
                     filterGroupId: filterGroupFilter.filterGroupId,
                     id: o.id!,
                     vendor: o.vendor,
                     product: o.product
                  });
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      )

   }


   // DELETE /companies/:companyId/filter-groups/:filterGroupId/filters
   public deleteAssetGroupFilters(companyId: string, filterGroupId: string): Observable<void> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<void>(

         s => {

            this.api.call<void, void>(`/companies/${companyId}/filter-groups/${filterGroupId}/filters`, undefined, "DELETE").subscribe({

               next: o => {
                  s.next();
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }


   // DELETE /companies/:companyId/filter-groups/:filterGroupId/filters/:filterId
   public deleteAssetGroupFilter(companyId: string, filterGroupId: string, filterId: string): Observable<void> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<void>(

         s => {

            this.api.call<void, void>(`/companies/${companyId}/filter-groups/${filterGroupId}/filters/${filterId}`, undefined, "DELETE").subscribe({

               next: o => {
                  s.next();
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }



   // GET /companies/:companyId/filter-groups/:filterGroupId/filters/:filterId/descs
   public listAssetGroupsFilterDescs(companyId: string, filterGroupId: string, filterId: string): Observable<CompanyAssetGroupFilterDescModel[]> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupFilterDescModel[]>(

         s => {

            this.api.call<void, FilterDescsDTO>(`/companies/${companyId}/filter-groups/${filterGroupId}/filters/${filterId}/descs`).subscribe({

               next: o => {

                  const fgfdms: CompanyAssetGroupFilterDescModel[] = o.filterDescs.map(fgfd => {
                     return {
                        _seq_no: fgfd._seq_no!,
                        _primary_term: fgfd._primary_term!,
                        companyId: companyId,
                        filterGroupId: filterGroupId,
                        filterId: filterId,
                        id: fgfd.id!,
                        field: fgfd.field,
                        operator: fgfd.operator,
                        value: fgfd.value,
                        negate: fgfd.negate
                     }
                  });
                  s.next(fgfdms);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      )

   }

   // POST /companies/:companyId/filter-groups/:filterGroupId/filters/:filterId/descs
   public createAssetGroupFilterDesc(filterGroupFilterDesc: CompanyAssetGroupFilterDescModel): Observable<CompanyAssetGroupFilterDescModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupFilterDescModel>(

         s => {

            const data: FilterDescDTO = {
               field: filterGroupFilterDesc.field,
               operator: filterGroupFilterDesc.operator,
               value: filterGroupFilterDesc.value,
               negate: filterGroupFilterDesc.negate
            };

            this.api.call<FilterDescDTO, FilterDescDTO>(`/companies/${filterGroupFilterDesc.companyId}/filter-groups/${filterGroupFilterDesc.filterGroupId}/filters/${filterGroupFilterDesc.filterId}/descs`, undefined, "POST", data).subscribe({

               next: o => {

                  const fgfm: CompanyAssetGroupFilterDescModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: filterGroupFilterDesc.companyId,
                     filterGroupId: filterGroupFilterDesc.filterGroupId,
                     filterId: filterGroupFilterDesc.filterId,
                     id: o.id!,
                     field: o.field,
                     operator: o.operator,
                     value: o.value,
                     negate: o.negate
                  }
                  s.next(fgfm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // GET /companies/:companyId/filter-groups/:filterGroupId/filters/:filterId/descs/:filterDescId
   public readAssetGroupFilterDesc(companyId: string, filterGroupId: string, filterId: string, filterDescId: string): Observable<CompanyAssetGroupFilterDescModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupFilterDescModel>(

         s => {

            this.api.call<void, FilterDescDTO>(`/companies/${companyId}/filter-groups/${filterGroupId}/filters/${filterId}/descs/${filterDescId}`).subscribe({

               next: o => {

                  const fgfdm: CompanyAssetGroupFilterDescModel = {
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: companyId,
                     filterGroupId: filterGroupId,
                     filterId: filterId,
                     id: o.id!,
                     field: o.field,
                     operator: o.operator,
                     value: o.value,
                     negate: o.negate
                  }
                  s.next(fgfdm);
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }

   // POST /companies/:companyId/filter-groups/:filterGroupId/filters/:filterId/descs/:filterDescId
   public updateAssetGroupFilterDesc(filterGroupFilterDesc: CompanyAssetGroupFilterDescModel): Observable<CompanyAssetGroupFilterDescModel> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<CompanyAssetGroupFilterDescModel>(

         s => {

            const data: FilterDescDTO = {
               _seq_no: filterGroupFilterDesc._seq_no,
               _primary_term: filterGroupFilterDesc._primary_term,
               id: filterGroupFilterDesc.id,
               field: filterGroupFilterDesc.field,
               operator: filterGroupFilterDesc.operator,
               value: filterGroupFilterDesc.value,
               negate: filterGroupFilterDesc.negate
            };

            this.api.call<FilterDescDTO, FilterDescDTO>(`/companies/${filterGroupFilterDesc.companyId}/filter-groups/${filterGroupFilterDesc.filterGroupId}/filters/${filterGroupFilterDesc.filterId}/descs/${filterGroupFilterDesc.id}`, undefined, "POST", data).subscribe({

               next: o => {

                  s.next({
                     _seq_no: o._seq_no!,
                     _primary_term: o._primary_term!,
                     companyId: filterGroupFilterDesc.companyId,
                     filterGroupId: filterGroupFilterDesc.filterGroupId,
                     filterId: filterGroupFilterDesc.filterId,
                     id: o.id!,
                     field: o.field,
                     operator: o.operator,
                     value: o.value,
                     negate: o.negate
                  });
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      )

   }

   // DELETE /companies/:companyId/filter-groups/:filterGroupId/filters/:filterId/descs/:filterDescId
   public deleteAssetGroupFilterDesc(companyId: string, filterGroupId: string, filterId: string, filterDescId: string): Observable<void> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return new Observable<void>(

         s => {

            this.api.call<void, void>(`/companies/${companyId}/filter-groups/${filterGroupId}/filters/${filterId}/descs/${filterDescId}`, undefined, "DELETE").subscribe({

               next: o => {

                  s.next();
                  s.complete();

               },

               error: e => {
                  s.error(e);
               }

            });

         }

      );

   }



   // GET /companies/:companyId/cve/:filterGroupId
   public getCveList(companyId: string, filterGroupId: string, from: number = 0, dateStart?: string, dateEnd?: string):  Observable<CveListDTO> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      let query = `?from=${from}`;
      if (dateStart || dateEnd) query += `&`;
      if (dateStart) query += `dateStart=${dateStart}&`;
      if (dateEnd) query += `dateEnd=${dateEnd}&`;

      return this.api.call<void, CveListDTO>(`/companies/${companyId}/filter-groups/${filterGroupId}/cves${query}`);

   }


   // GET /companies/:companyId/cve/:cve/ac
   public getCveAffectedConfigurations(companyId: string, cve: string): Observable<any[]> {

      if (this.api.status !== ApiStatus.Ready) throw new Error("API not initialized");

      return this.api.call<void, any[]>(`/companies/${companyId}/cve/${cve}/ac`);


   }


}
